import { useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import map_filter_atom from '../../atom/map-filter'
import get_map_cluster_selector from '../../selector/get-map-cluster-selector'
import { EstateModel } from '../../selector/get-map-data-selector'
import { SIGHT } from '../../shared/constant'
import { encSha256 } from '../../shared/passport'

interface Props {
  map_ref: React.MutableRefObject<any>
  setFold: React.Dispatch<React.SetStateAction<boolean>>
}

interface GuOverlayGeneratorProps {
  lat: number
  lng: number
  count: number
  level: number;
  lat_min?: number;
  lat_max?: number;
  lng_min?: number;
  lng_max?: number;
}

interface MatrixModel {
  lat_min: number
  lng_min: number
  lat_max: number
  lng_max: number
  count: number
}

interface MapperModel {
  lat: number;
  lng: number;
  count: number;
  lat_min: number;
  lat_max: number;
  lng_min: number;
  lng_max: number;
}

const util = {
  guOverlayGenerator: (props: GuOverlayGeneratorProps) => {
    // [2025-06-10] by 새날_방용환
    // - 변경: lat_min, lat_max, lng_min, lng_max 추가
    // - 목적: map-side-bar-list.tsx에서 마우스 hover한 매물의 원형마커 class 변경을 위한 변수
    const { lat, lng, count, level, lat_min, lat_max, lng_min, lng_max } = props;

    const id = encSha256(`${lat}/${lng}`)

    // [2025-06-09] by 새날_방용환
    // - 기존: zoom에 상관 없이 원형 마커 클릭 시 해당 위치로 이동
    // - 변경: zoom이 500m 이하일 때 클릭 이벤트 제거
    // - 목적: 원형 마커 클릭 시 페이지 새로고침으로 기본값인 500m로 변경됨에 따라 500m 이하일 때는 클릭 이벤트 제거
    // [2025-06-11] by 새날_방용환
    // - 변경: 지도 zoom level과 상관 없이 원형 마커 클릭 시 좌측 리스트 펼침
    // - 참고: zoom level 7(1km) 이상에서 클릭 시 500m로 변경 되기에 리스트 펼쳐져도 괜찮음
    const onclickAttr = level > 6 ? `onclick="window._gc(this); window._openSidebar();"` : `onclick="window._openSidebar();"`;

    // [2025-06-10] by 새날_방용환
    // - 변경: data-loc_lat_min, data-loc_lat_max, data-loc_lng_min, data-loc_lng_max 추가
    // - 목적: map-side-bar-list.tsx에서 마우스 hover한 매물의 원형마커 class 변경을 위한 변수
    return `
      <div
        ${onclickAttr}
        id="gu_${id}"
        data-loc_lat="${lat}"
        data-loc_lng="${lng}"
        data-loc_min_lat="${lat_min ?? ''}"
        data-loc_max_lat="${lat_max ?? ''}"
        data-loc_min_lng="${lng_min ?? ''}"
        data-loc_max_lng="${lng_max ?? ''}"
        class="gu"
      >
        ${count}
      </div>
    `;
  },
}

/* eslint-disable @typescript-eslint/no-unused-vars */
const MapCluster = (props: Props) => {
  const map = props.map_ref.current

  const map_filter = useRecoilValue(map_filter_atom)

  const data = useRecoilValue(get_map_cluster_selector)

  const { ne_lat, sw_lat, ne_lng, sw_lng } = useRecoilValue(map_filter_atom)

  const { sub_division } = map_filter.deal_kind

  const gu_overlays = useRef<Array<any>>([])

  const guDrawing = () => {
    try {
      const level = map.getLevel()

      if (
        SIGHT.ICONS.min <= level &&
        SIGHT.GU.max >= level &&
        sub_division === 'N'
      ) {

        // [2025-06-05] by 새날_방용환
        // - 기존: grid 8로 고정
        // - 변경: 500m 이하일 때 grid 32, 500m 초과일 때 grid 6
        // - 목적: 맵을 움직일 때 원 마커가 움직여서 500m이하일 때 더 잘게 쪼개기
        let grid = 8;
        if(level <= SIGHT.ICONS.max) {
          grid = 32;
        } else {
          grid = 6;
        }

        const matrix: Array<Array<MatrixModel>> = Array(grid)
          .fill(1)
          .map((_, lat_index) => {
            return Array(grid)
              .fill(1)
              .map((_, lng_index) => {
                const lng = sw_lng + ((ne_lng - sw_lng) / grid) * lng_index
                const lat = sw_lat + ((ne_lat - sw_lat) / grid) * lat_index

                const lng_step = (ne_lng - sw_lng) / grid
                const lat_step = (ne_lat - sw_lat) / grid

                return {
                  lat_min: lat,
                  lng_min: lng,
                  lat_max: lat + lat_step,
                  lng_max: lng + lng_step,
                  count: 0,
                }
              })
          })

          const mapper: Array<Array<MapperModel>> = matrix.map((i) =>
          i.map((matrix) => {
            const matcher = data.filter(
              (i) =>
                i.lat >= matrix.lat_min &&
                i.lat <= matrix.lat_max &&
                i.lng >= matrix.lng_min &&
                i.lng <= matrix.lng_max &&
                i.status !== 'COMPLETE' &&
                i.deal_kind_text !== '분양'
            )

            if (matcher.length === 0)
              return {
                lat: -1,
                lng: -1,
                count: 0,
                lat_min: matrix.lat_min,
                lat_max: matrix.lat_max,
                lng_min: matrix.lng_min,
                lng_max: matrix.lng_max,
              }

            const lat =
              matcher.reduce((acc: number, current: EstateModel) => {
                return acc + current.lat
              }, 0) / matcher.length
            const lng =
              matcher.reduce((acc: number, current: EstateModel) => {
                return acc + current.lng
              }, 0) / matcher.length

            return {
              lat,
              lng,
              count: matcher.length,
              lat_min: matrix.lat_min,
              lat_max: matrix.lat_max,
              lng_min: matrix.lng_min,
              lng_max: matrix.lng_max,
            }
          })
        )

        for (const item of mapper.flat()) {
          const {
            lat,
            lng,
            count,
            lat_min,
            lat_max,
            lng_min,
            lng_max,
          } = item;

          const position = new window.kakao.maps.LatLng(lat, lng)

          const overlay = new window.kakao.maps.CustomOverlay({
            position,
            content: util.guOverlayGenerator({
              lat,
              lng,
              count,
              level,
              lat_min,
              lat_max,
              lng_min,
              lng_max,
            }),
          })

          overlay.setMap(map)
          overlay.setZIndex(4);

          gu_overlays.current.push(overlay)
        }

        return
      }
    } catch {}
  }

  const guRemove = () => {
    for (const overlay of gu_overlays.current) {
      overlay.setMap(null)
    }
  }

  useEffect(() => {
    if (map) {
      guDrawing()
    }

    return () => {
      guRemove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, map])

  // [2025-06-11] by 새날_방용환
  // - return 되는 문자열 내부의 함수가 쓰여질 수 있게 window에 전역 함수로 등록
  useEffect(() => {
    (window as any)._openSidebar = () => {
      props.setFold(false)
    }
  }, [props.setFold])

  // [2025-06-12] by 새날_방용환
  // - 원형 마커에 마우스 hover 시 위경도 최대 최소 값을 가지고 map-side-bar-list에서 해당 범위에 있는 li background 색상 변경 시키기
  useEffect(() => {
    const handleGuHover = (e: MouseEvent) => {
      const el = e.currentTarget as HTMLElement;

      const latMin = parseFloat(el.getAttribute('data-loc_min_lat') || '0');
      const latMax = parseFloat(el.getAttribute('data-loc_max_lat') || '0');
      const lngMin = parseFloat(el.getAttribute('data-loc_min_lng') || '0');
      const lngMax = parseFloat(el.getAttribute('data-loc_max_lng') || '0');

      const listItems = document.querySelectorAll<HTMLLIElement>('li[data-list-lat][data-list-lng]');

      listItems.forEach(li => {
        const lat = parseFloat(li.getAttribute('data-list-lat') || '0');
        const lng = parseFloat(li.getAttribute('data-list-lng') || '0');

        if (
          lat >= latMin && lat <= latMax &&
          lng >= lngMin && lng <= lngMax
        ) {
          li.style.background = '#e2e4f1a3';
        } else {
          li.style.background = '';
        }
      });
    };

    // 마커가 추가된 후에 실행되어야 함
    const bindHoverEventToGuMarkers = () => {
      const markers = document.querySelectorAll<HTMLElement>('.gu');
      markers.forEach(marker => {
        marker.addEventListener('mouseenter', handleGuHover);
        marker.addEventListener('mouseleave', () => {
          const listItems = document.querySelectorAll<HTMLLIElement>('li[data-list-lat][data-list-lng]');
          listItems.forEach(li => (li.style.background = ''));
        });
      });
    };

    // 약간의 지연 후 마커 DOM 렌더링 완료된 시점에 이벤트 바인딩
    const timeout = setTimeout(() => {
      bindHoverEventToGuMarkers();
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [data]);

  return <></>
}

export default MapCluster
